import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import CourseChips from '../Subjects/parts/CourseChips';
import AddChapterModal from './parts/AddChapterModal';
import AddModuleModal from './parts/AddModuleModal';
import AddStudyMaterialModal from './parts/Addstudymaterialmodal';
import MaterialDetailsModal from './parts/MaterialDetailsModal';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface HLSVideo {
  video_id: string;
  original_name: string;
  m3u8_url: string | null;
  size: number;
  status: string;
}

interface StudyMaterial {
  id: string;
  chapter_id: string;
  module_id: string;
  course_id: string;
  institute_id: string;
  title: string;
  body: string;
  order: number;
  status: string;
  files: StudyMaterialFile[];
  video_links: string[];
  videos: HLSVideo[];
  files_count: number;
  videos_count: number;
  created_at: string;
  created_by: string;  // ✅ add this
}

interface Chapter {
  id: string;
  module_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  study_materials?: StudyMaterial[];
  materials_count: number;
}

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  chapters: Chapter[];
  chapters_count: number;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

// ── Component ──────────────────────────────────────────────────────────────────

const StudyMaterials: React.FC = () => {
  const toast    = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});

  const [modules,           setModules]           = useState<Module[]>([]);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState<string | null>(null);
  const [selectedCourseId,  setSelectedCourseId]  = useState<string>('');

  const [showAddModuleModal,   setShowAddModuleModal]   = useState(false);
  const [showAddChapterModal,  setShowAddChapterModal]  = useState(false);
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [showDetailsModal,     setShowDetailsModal]     = useState(false);

  const [selectedModule,   setSelectedModule]   = useState<Module | null>(null);
  const [selectedChapter,  setSelectedChapter]  = useState<Chapter | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);

  const [deleteDialog, setDeleteDialog] = useState<{
    visible: boolean;
    type: 'module' | 'chapter' | 'material';
    id: string;
    title: string;
  }>({ visible: false, type: 'material', id: '', title: '' });

  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();

  useEffect(() => {
    if (!dashboardData) fetchDashboardData();
  }, [dashboardData, fetchDashboardData]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .p-menu { border-radius: 12px !important; border: 1px solid #e0e0e0 !important; box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important; padding: 8px !important; }
      .p-menu .p-menuitem-link { border-radius: 8px !important; padding: 10px 12px !important; margin: 2px 0 !important; transition: all 0.2s ease !important; }
      .p-menu .p-menuitem-link:hover { background: #f5f5f5 !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (selectedCourseId && dashboardData?.institutes?.[0]?.institute_id) {
      fetchCourseStructure();
    } else {
      setModules([]);
    }
  }, [selectedCourseId, dashboardData]);

  const fetchCourseStructure = async () => {
    if (!dashboardData?.institutes?.[0]?.institute_id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/study-materials/course/${selectedCourseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
      } else {
        setError('Failed to load course structure');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load course structure');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect    = (course: Course) => setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
  const handleAddModule       = () => { setSelectedModule(null); setShowAddModuleModal(true); };
  const handleEditModule      = (module: Module) => { setSelectedModule(module); setShowAddModuleModal(true); };
  const handleDeleteModule    = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'module', id, title });
  const handleAddChapter      = (module: Module) => { setSelectedModule(module); setSelectedChapter(null); setShowAddChapterModal(true); };
  const handleEditChapter     = (chapter: Chapter, module: Module) => { setSelectedModule(module); setSelectedChapter(chapter); setShowAddChapterModal(true); };
  const handleDeleteChapter   = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'chapter', id, title });
  const handleAddMaterial     = (chapter: Chapter) => { setSelectedChapter(chapter); setSelectedMaterial(null); setShowAddMaterialModal(true); };
  const handleViewMaterial    = (material: StudyMaterial) => { setSelectedMaterial(material); setShowDetailsModal(true); };
  const handleEditMaterial    = (material: StudyMaterial) => { setSelectedMaterial(material); setShowAddMaterialModal(true); };
  const handleDeleteMaterial  = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'material', id, title });

  const confirmDelete = async () => {
    try {
      const endpoints: Record<string, string> = {
        module:   `/v1/modules/${deleteDialog.id}`,
        chapter:  `/v1/chapters/${deleteDialog.id}`,
        material: `/v1/study-materials/${deleteDialog.id}`,
      };
      await apiClient.delete(endpoints[deleteDialog.type]);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `${deleteDialog.type} deleted successfully`, life: 3000 });
      fetchCourseStructure();
      setDeleteDialog({ visible: false, type: 'material', id: '', title: '' });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete', life: 4000 });
    }
  };

  const getModuleMenuItems   = (module: Module)               => [
    { label: 'Add Chapter',   icon: 'pi pi-plus',   command: () => handleAddChapter(module)  },
    { label: 'Edit Module',   icon: 'pi pi-pencil', command: () => handleEditModule(module)  },
    { label: 'Delete Module', icon: 'pi pi-trash',  command: () => handleDeleteModule(module.id, module.title) },
  ];
  const getChapterMenuItems  = (chapter: Chapter, module: Module) => [
    { label: 'Add Material',   icon: 'pi pi-plus',   command: () => handleAddMaterial(chapter)             },
    { label: 'Edit Chapter',   icon: 'pi pi-pencil', command: () => handleEditChapter(chapter, module)     },
    { label: 'Delete Chapter', icon: 'pi pi-trash',  command: () => handleDeleteChapter(chapter.id, chapter.title) },
  ];
  const getMaterialMenuItems = (material: StudyMaterial) => [
    { label: 'View Details', icon: 'pi pi-eye',    command: () => handleViewMaterial(material) },
    { label: 'Edit',         icon: 'pi pi-pencil', command: () => handleEditMaterial(material) },
    { label: 'Delete',       icon: 'pi pi-trash',  command: () => handleDeleteMaterial(material.id, material.title) },
  ];

  if (dashboardLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
        <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="warn" text="Unable to load dashboard data. Please refresh the page." style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, type: 'material', id: '', title: '' })}
        onConfirm={confirmDelete}
        title={`Delete ${deleteDialog.type.charAt(0).toUpperCase() + deleteDialog.type.slice(1)}`}
        message={`Are you sure you want to delete "${deleteDialog.title}"? ${deleteDialog.type !== 'material' ? 'All child content will also be deleted.' : 'This action cannot be undone.'}`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)' }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Study Material Management
              </div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>Course Content Structure</h1>
              <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>Organize content with Modules → Chapters → Study Materials</p>
              <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                <i className="pi pi-book" style={{ marginRight: '6px' }}></i>
                {modules.length} {selectedCourseId ? 'Modules' : 'Select a course'}
              </div>
            </div>
            <GlobalButton label="Add Module" variant="success" icon="pi pi-plus" onClick={handleAddModule} disabled={!selectedCourseId} />
          </div>
        </div>
      </div>

      {/* Course Filter */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <i className="pi pi-filter" style={{ color: '#0f3d3e', fontSize: '1rem' }}></i>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>Select Course to Manage Content</h3>
          {selectedCourseId && (
            <button onClick={() => setSelectedCourseId('')} style={{ marginLeft: 'auto', padding: '6px 12px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '8px', color: '#666', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="pi pi-times" style={{ fontSize: '0.75rem' }}></i> Clear Selection
            </button>
          )}
        </div>
        <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />}

      {/* Content */}
      {!selectedCourseId ? (
        <EmptyState icon="pi pi-book" title="Select a Course" subtitle="Click on a course chip above to manage modules and content" />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
            <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading course structure...</p>
          </div>
        </div>
      ) : modules.length === 0 ? (
        <EmptyState icon="pi pi-sitemap" title="No modules found" subtitle="Start by creating your first module">
          <GlobalButton label="Add First Module" variant="primary" icon="pi pi-plus" onClick={handleAddModule} />
        </EmptyState>
      ) : (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
          {modules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              menuRefs={menuRefs}
              menuItems={getModuleMenuItems(module)}
              chapterMenuItems={(ch) => getChapterMenuItems(ch, module)}
              materialMenuItems={getMaterialMenuItems}
              onViewMaterial={handleViewMaterial}
            />
          ))}
        </div>
      )}

      {selectedMaterial && (
        <MaterialDetailsModal
          visible={showDetailsModal}
          material={selectedMaterial}
          onHide={() => setShowDetailsModal(false)}
          onEdit={() => { setShowDetailsModal(false); setShowAddMaterialModal(true); }}
        />
      )}

      <AddModuleModal
        visible={showAddModuleModal}
        courseId={selectedCourseId}
        editingModule={selectedModule}
        onHide={() => { setShowAddModuleModal(false); setSelectedModule(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedModule ? 'Module updated!' : 'Module created!', life: 3000 });
          fetchCourseStructure();
          setShowAddModuleModal(false);
          setSelectedModule(null);
        }}
      />

      <AddChapterModal
        visible={showAddChapterModal}
        moduleId={selectedModule?.id || ''}
        editingChapter={selectedChapter}
        onHide={() => { setShowAddChapterModal(false); setSelectedChapter(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedChapter ? 'Chapter updated!' : 'Chapter created!', life: 3000 });
          fetchCourseStructure();
          setShowAddChapterModal(false);
          setSelectedChapter(null);
        }}
      />

      <AddStudyMaterialModal
        visible={showAddMaterialModal}
        chapterId={selectedChapter?.id || ''}
        editingMaterial={selectedMaterial}
        onHide={() => { setShowAddMaterialModal(false); setSelectedMaterial(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedMaterial ? 'Material updated!' : 'Material created!', life: 3000 });
          fetchCourseStructure();
          setShowAddMaterialModal(false);
          setSelectedMaterial(null);
        }}
      />
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ icon: string; title: string; subtitle: string; children?: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
    <i className={icon} style={{ fontSize: '3.5rem', color: '#ddd', marginBottom: '16px', display: 'block' }}></i>
    <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>{title}</h3>
    <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>{subtitle}</p>
    {children}
  </div>
);

interface ModuleCardProps {
  module: Module;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  chapterMenuItems: (ch: Chapter) => any[];
  materialMenuItems: (m: StudyMaterial) => any[];
  onViewMaterial: (m: StudyMaterial) => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, menuRefs, menuItems, chapterMenuItems, materialMenuItems, onViewMaterial }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', border: '2px solid #e0e0e0', borderRadius: '16px', padding: '20px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem', fontWeight: '700', color: '#d4f17e' }}>
          {module.order}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', fontWeight: '600', color: '#1a1a1a' }}>{module.title}</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#666', lineHeight: '1.5' }}>{module.description}</p>
          <div style={{ fontSize: '0.8rem', color: '#999' }}>
            <i className="pi pi-book" style={{ marginRight: '4px' }}></i>
            {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
          </div>
        </div>
        <MenuButton menuKey={`module-${module.id}`} menuRefs={menuRefs} menuItems={menuItems} size="normal" />
      </div>

      {module.chapters.length > 0 && (
        <div style={{ marginTop: '20px', paddingLeft: '66px' }}>
          {module.chapters.map((chapter) => (
            <ChapterCard
              key={chapter.id}
              chapter={chapter}
              moduleOrder={module.order}
              menuRefs={menuRefs}
              menuItems={chapterMenuItems(chapter)}
              materialMenuItems={materialMenuItems}
              onViewMaterial={onViewMaterial}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

interface ChapterCardProps {
  chapter: Chapter;
  moduleOrder: number;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  materialMenuItems: (m: StudyMaterial) => any[];
  onViewMaterial: (m: StudyMaterial) => void;
}

const ChapterCard: React.FC<ChapterCardProps> = ({ chapter, moduleOrder, menuRefs, menuItems, materialMenuItems, onViewMaterial }) => (
  <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
        {moduleOrder}.{chapter.order}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>{chapter.title}</h4>
        <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#666' }}>{chapter.description}</p>
        <div style={{ fontSize: '0.75rem', color: '#999' }}>
          <i className="pi pi-file" style={{ marginRight: '4px' }}></i>
          {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
        </div>

        {chapter.study_materials && chapter.study_materials.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {chapter.study_materials.map((material) => (
              <MaterialRow
                key={material.id}
                material={material}
                chapterOrder={chapter.order}
                moduleOrder={moduleOrder}
                menuRefs={menuRefs}
                menuItems={materialMenuItems(material)}
                onView={() => onViewMaterial(material)}
              />
            ))}
          </div>
        )}
      </div>
      <MenuButton menuKey={`chapter-${chapter.id}`} menuRefs={menuRefs} menuItems={menuItems} size="small" />
    </div>
  </div>
);

interface MaterialRowProps {
  material: StudyMaterial;
  chapterOrder: number;   // ✅ added
  moduleOrder: number;    // ✅ added
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  onView: () => void;
}

const MaterialRow: React.FC<MaterialRowProps> = ({ material, chapterOrder, moduleOrder, menuRefs, menuItems, onView }) => {
  const readyVideos      = material.videos?.filter(v => v.status === 'ready').length      ?? 0;
  const processingVideos = material.videos?.filter(v => v.status === 'processing').length ?? 0;

  return (
    <div
      onClick={onView}
      style={{ padding: '12px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#0f3d3e'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>

        {/* Order badge */}
        <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#e8f4f8', border: '1px solid #b8dde8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: '700', color: '#0f3d3e' }}>
          {moduleOrder}.{chapterOrder}.{material.order ?? 1}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1a1a1a', marginBottom: '6px' }}>{material.title}</div>
          <div style={{ fontSize: '0.75rem', color: '#999', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {material.files_count > 0 && (
              <span><i className="pi pi-paperclip" style={{ marginRight: '4px' }}></i>{material.files_count} file{material.files_count !== 1 ? 's' : ''}</span>
            )}
            {readyVideos > 0 && (
              <span style={{ color: '#16a34a', fontWeight: '600' }}>
                <i className="pi pi-play-circle" style={{ marginRight: '4px' }}></i>{readyVideos} video{readyVideos !== 1 ? 's' : ''}
              </span>
            )}
            {processingVideos > 0 && (
              <span style={{ color: '#d97706' }}>
                <i className="pi pi-spin pi-spinner" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{processingVideos} processing
              </span>
            )}
          </div>
        </div>

        <MenuButton menuKey={`material-${material.id}`} menuRefs={menuRefs} menuItems={menuItems} size="small" />
      </div>
    </div>
  );
};

interface MenuButtonProps {
  menuKey: string;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  size: 'normal' | 'small';
}

const MenuButton: React.FC<MenuButtonProps> = ({ menuKey, menuRefs, menuItems, size }) => (
  <>
    <button
      onClick={(e) => { e.stopPropagation(); menuRefs.current[menuKey]?.toggle(e); }}
      style={{ padding: size === 'normal' ? '8px 12px' : '6px 10px', background: '#f5f5f5', border: size === 'small' ? '1px solid #e0e0e0' : 'none', borderRadius: size === 'normal' ? '8px' : '6px', color: '#0f3d3e', cursor: 'pointer', flexShrink: 0 }}
    >
      <i className="pi pi-ellipsis-v" style={{ fontSize: size === 'normal' ? '0.85rem' : '0.75rem' }}></i>
    </button>
    <Menu model={menuItems} popup ref={(el) => { menuRefs.current[menuKey] = el; }} />
  </>
);

export default StudyMaterials;


// import React, { useState, useEffect, useRef } from 'react';
// import { Toast } from 'primereact/toast';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Message } from 'primereact/message';
// import { Dialog } from 'primereact/dialog';
// import { Menu } from 'primereact/menu';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import CustomConfirmDialog from '../../components/CustomConfirmDialog';
// import CourseChips from '../Subjects/parts/CourseChips';
// import AddChapterModal from './parts/AddChapterModal';
// import AddModuleModal from './parts/AddModuleModal';
// import AddStudyMaterialModal from './parts/Addstudymaterialmodal';
// import MaterialDetailsModal from './parts/MaterialDetailsModal';

// // ── Types ─────────────────────────────────────────────────────────────────────

// interface StudyMaterialFile {
//   file_name: string;
//   key: string;
//   url: string;
//   size: number;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface HLSVideo {
//   video_id: string;
//   original_name: string;
//   m3u8_url: string | null;
//   size: number;
//   status: string; // 'ready' | 'processing' | 'failed'
// }

// interface StudyMaterial {
//   id: string;
//   chapter_id: string;
//   module_id: string;
//   course_id: string;
//   institute_id: string;
//   title: string;
//   body: string;
//   status: string;
//   files: StudyMaterialFile[];
//   video_links: string[];
//   videos: HLSVideo[];
//   files_count: number;
//   videos_count: number;
//   created_at: string;
// }

// interface Chapter {
//   id: string;
//   module_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   study_materials?: StudyMaterial[];
//   materials_count: number;
// }

// interface Module {
//   id: string;
//   course_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   chapters: Chapter[];
//   chapters_count: number;
// }

// interface Course {
//   course_id: string;
//   name: string;
//   title: string;
//   status: string;
// }

// // ── Component ──────────────────────────────────────────────────────────────────

// const StudyMaterials: React.FC = () => {
//   const toast    = useRef<Toast>(null);
//   const menuRefs = useRef<{ [key: string]: Menu | null }>({});

//   const [modules,           setModules]           = useState<Module[]>([]);
//   const [loading,           setLoading]           = useState(false);
//   const [error,             setError]             = useState<string | null>(null);
//   const [selectedCourseId,  setSelectedCourseId]  = useState<string>('');

//   // Modals
//   const [showAddModuleModal,   setShowAddModuleModal]   = useState(false);
//   const [showAddChapterModal,  setShowAddChapterModal]  = useState(false);
//   const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
//   const [showDetailsModal,     setShowDetailsModal]     = useState(false);

//   // Selected items
//   const [selectedModule,   setSelectedModule]   = useState<Module | null>(null);
//   const [selectedChapter,  setSelectedChapter]  = useState<Chapter | null>(null);
//   const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);

//   // Delete dialog
//   const [deleteDialog, setDeleteDialog] = useState<{
//     visible: boolean;
//     type: 'module' | 'chapter' | 'material';
//     id: string;
//     title: string;
//   }>({ visible: false, type: 'material', id: '', title: '' });

//   const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();

//   useEffect(() => {
//     if (!dashboardData) fetchDashboardData();
//   }, [dashboardData, fetchDashboardData]);

//   // ── Styles injection ──────────────────────────────────────────────────────
//   useEffect(() => {
//     const style = document.createElement('style');
//     style.innerHTML = `
//       .p-menu {
//         border-radius: 12px !important;
//         border: 1px solid #e0e0e0 !important;
//         box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
//         padding: 8px !important;
//       }
//       .p-menu .p-menuitem-link {
//         border-radius: 8px !important;
//         padding: 10px 12px !important;
//         margin: 2px 0 !important;
//         transition: all 0.2s ease !important;
//       }
//       .p-menu .p-menuitem-link:hover { background: #f5f5f5 !important; }
//     `;
//     document.head.appendChild(style);
//     return () => { document.head.removeChild(style); };
//   }, []);

//   useEffect(() => {
//     if (selectedCourseId && dashboardData?.institutes?.[0]?.institute_id) {
//       fetchCourseStructure();
//     } else {
//       setModules([]);
//     }
//   }, [selectedCourseId, dashboardData]);

//   // ── Data fetching ─────────────────────────────────────────────────────────
//   const fetchCourseStructure = async () => {
//     if (!dashboardData?.institutes?.[0]?.institute_id) return;
//     try {
//       setLoading(true);
//       setError(null);
//       const response = await apiClient.get(`/v1/study-materials/course/${selectedCourseId}/structure`);
//       if (response.data.success) {
//         setModules(response.data.data.modules || []);
//       } else {
//         setError('Failed to load course structure');
//       }
//     } catch (err: any) {
//       setError(err.response?.data?.error || 'Failed to load course structure');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Handlers ──────────────────────────────────────────────────────────────
//   const handleCourseSelect = (course: Course) => {
//     setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
//   };

//   const handleAddModule = () => { setSelectedModule(null); setShowAddModuleModal(true); };
//   const handleEditModule = (module: Module) => { setSelectedModule(module); setShowAddModuleModal(true); };
//   const handleDeleteModule = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'module', id, title });

//   const handleAddChapter = (module: Module) => { setSelectedModule(module); setSelectedChapter(null); setShowAddChapterModal(true); };
//   const handleEditChapter = (chapter: Chapter, module: Module) => { setSelectedModule(module); setSelectedChapter(chapter); setShowAddChapterModal(true); };
//   const handleDeleteChapter = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'chapter', id, title });

//   const handleAddMaterial = (chapter: Chapter) => { setSelectedChapter(chapter); setSelectedMaterial(null); setShowAddMaterialModal(true); };
//   const handleViewMaterial = (material: StudyMaterial) => { setSelectedMaterial(material); setShowDetailsModal(true); };
//   const handleEditMaterial = (material: StudyMaterial) => { setSelectedMaterial(material); setShowAddMaterialModal(true); };
//   const handleDeleteMaterial = (id: string, title: string) => setDeleteDialog({ visible: true, type: 'material', id, title });

//   const confirmDelete = async () => {
//     try {
//       const endpoints: Record<string, string> = {
//         module:   `/v1/modules/${deleteDialog.id}`,
//         chapter:  `/v1/chapters/${deleteDialog.id}`,
//         material: `/v1/study-materials/${deleteDialog.id}`,
//       };
//       await apiClient.delete(endpoints[deleteDialog.type]);
//       toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `${deleteDialog.type} deleted successfully`, life: 3000 });
//       fetchCourseStructure();
//       setDeleteDialog({ visible: false, type: 'material', id: '', title: '' });
//     } catch (err: any) {
//       toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete', life: 4000 });
//     }
//   };

//   // ── Menu items ────────────────────────────────────────────────────────────
//   const getModuleMenuItems = (module: Module) => [
//     { label: 'Add Chapter',   icon: 'pi pi-plus',   command: () => handleAddChapter(module) },
//     { label: 'Edit Module',   icon: 'pi pi-pencil', command: () => handleEditModule(module) },
//     { label: 'Delete Module', icon: 'pi pi-trash',  command: () => handleDeleteModule(module.id, module.title) },
//   ];

//   const getChapterMenuItems = (chapter: Chapter, module: Module) => [
//     { label: 'Add Material',    icon: 'pi pi-plus',   command: () => handleAddMaterial(chapter) },
//     { label: 'Edit Chapter',    icon: 'pi pi-pencil', command: () => handleEditChapter(chapter, module) },
//     { label: 'Delete Chapter',  icon: 'pi pi-trash',  command: () => handleDeleteChapter(chapter.id, chapter.title) },
//   ];

//   const getMaterialMenuItems = (material: StudyMaterial) => [
//     { label: 'View Details', icon: 'pi pi-eye',    command: () => handleViewMaterial(material) },
//     { label: 'Edit',         icon: 'pi pi-pencil', command: () => handleEditMaterial(material) },
//     { label: 'Delete',       icon: 'pi pi-trash',  command: () => handleDeleteMaterial(material.id, material.title) },
//   ];

//   // ── Loading / Error states ────────────────────────────────────────────────
//   if (dashboardLoading) {
//     return (
//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
//         <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
//         <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading dashboard data...</p>
//       </div>
//     );
//   }

//   if (!dashboardData) {
//     return (
//       <div style={{ padding: '24px' }}>
//         <Message severity="warn" text="Unable to load dashboard data. Please refresh the page." style={{ fontSize: '0.9rem' }} />
//       </div>
//     );
//   }

//   // ── Render ────────────────────────────────────────────────────────────────
//   return (
//     <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
//       <Toast ref={toast} />

//       <CustomConfirmDialog
//         visible={deleteDialog.visible}
//         onHide={() => setDeleteDialog({ visible: false, type: 'material', id: '', title: '' })}
//         onConfirm={confirmDelete}
//         title={`Delete ${deleteDialog.type.charAt(0).toUpperCase() + deleteDialog.type.slice(1)}`}
//         message={`Are you sure you want to delete "${deleteDialog.title}"? ${deleteDialog.type !== 'material' ? 'All child content will also be deleted.' : 'This action cannot be undone.'}`}
//         confirmLabel="Delete"
//         cancelLabel="Cancel"
//         severity="danger"
//         icon="pi pi-trash"
//       />

//       {/* ── Header ── */}
//       <div style={{
//         background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//         borderRadius: '20px', padding: '32px', marginBottom: '24px',
//         color: 'white', position: 'relative', overflow: 'hidden',
//         boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
//       }}>
//         <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
//         <div style={{ position: 'relative', zIndex: 1 }}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
//             <div>
//               <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
//                 Study Material Management
//               </div>
//               <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
//                 Course Content Structure
//               </h1>
//               <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
//                 Organize content with Modules → Chapters → Study Materials
//               </p>
//               <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
//                 <i className="pi pi-book" style={{ marginRight: '6px' }}></i>
//                 {modules.length} {selectedCourseId ? 'Modules' : 'Select a course'}
//               </div>
//             </div>
//             <GlobalButton label="Add Module" variant="success" icon="pi pi-plus" onClick={handleAddModule} disabled={!selectedCourseId} />
//           </div>
//         </div>
//       </div>

//       {/* ── Course Filter ── */}
//       <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
//           <i className="pi pi-filter" style={{ color: '#0f3d3e', fontSize: '1rem' }}></i>
//           <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>Select Course to Manage Content</h3>
//           {selectedCourseId && (
//             <button
//               onClick={() => setSelectedCourseId('')}
//               style={{ marginLeft: 'auto', padding: '6px 12px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '8px', color: '#666', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
//             >
//               <i className="pi pi-times" style={{ fontSize: '0.75rem' }}></i>
//               Clear Selection
//             </button>
//           )}
//         </div>
//         <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
//       </div>

//       {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />}

//       {/* ── Content ── */}
//       {!selectedCourseId ? (
//         <EmptyState icon="pi pi-book" title="Select a Course" subtitle="Click on a course chip above to manage modules and content" />
//       ) : loading ? (
//         <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px' }}>
//           <div style={{ textAlign: 'center' }}>
//             <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
//             <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading course structure...</p>
//           </div>
//         </div>
//       ) : modules.length === 0 ? (
//         <EmptyState icon="pi pi-sitemap" title="No modules found" subtitle="Start by creating your first module">
//           <GlobalButton label="Add First Module" variant="primary" icon="pi pi-plus" onClick={handleAddModule} />
//         </EmptyState>
//       ) : (
//         <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
//           {modules.map((module) => (
//             <ModuleCard
//               key={module.id}
//               module={module}
//               menuRefs={menuRefs}
//               menuItems={getModuleMenuItems(module)}
//               chapterMenuItems={(ch) => getChapterMenuItems(ch, module)}
//               materialMenuItems={getMaterialMenuItems}
//               onViewMaterial={handleViewMaterial}
//             />
//           ))}
//         </div>
//       )}

//       {/* ── Material Details Modal (split component) ── */}
//       {selectedMaterial && (
//         <MaterialDetailsModal
//           visible={showDetailsModal}
//           material={selectedMaterial}
//           onHide={() => setShowDetailsModal(false)}
//           onEdit={() => { setShowDetailsModal(false); setShowAddMaterialModal(true); }}
//         />
//       )}

//       {/* ── Add/Edit Modals ── */}
//       <AddModuleModal
//         visible={showAddModuleModal}
//         courseId={selectedCourseId}
//         editingModule={selectedModule}
//         onHide={() => { setShowAddModuleModal(false); setSelectedModule(null); }}
//         onSuccess={() => {
//           toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedModule ? 'Module updated!' : 'Module created!', life: 3000 });
//           fetchCourseStructure();
//           setShowAddModuleModal(false);
//           setSelectedModule(null);
//         }}
//       />

//       <AddChapterModal
//         visible={showAddChapterModal}
//         moduleId={selectedModule?.id || ''}
//         editingChapter={selectedChapter}
//         onHide={() => { setShowAddChapterModal(false); setSelectedChapter(null); }}
//         onSuccess={() => {
//           toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedChapter ? 'Chapter updated!' : 'Chapter created!', life: 3000 });
//           fetchCourseStructure();
//           setShowAddChapterModal(false);
//           setSelectedChapter(null);
//         }}
//       />

//       <AddStudyMaterialModal
//         visible={showAddMaterialModal}
//         chapterId={selectedChapter?.id || ''}
//         onHide={() => { setShowAddMaterialModal(false); setSelectedMaterial(null); }}
//         onSuccess={() => {
//           toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedMaterial ? 'Material updated!' : 'Material created!', life: 3000 });
//           fetchCourseStructure();
//           setShowAddMaterialModal(false);
//           setSelectedMaterial(null);
//         }}
//       />
//     </div>
//   );
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // Sub-components
// // ─────────────────────────────────────────────────────────────────────────────

// const EmptyState: React.FC<{ icon: string; title: string; subtitle: string; children?: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
//   <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
//     <i className={icon} style={{ fontSize: '3.5rem', color: '#ddd', marginBottom: '16px', display: 'block' }}></i>
//     <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>{title}</h3>
//     <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>{subtitle}</p>
//     {children}
//   </div>
// );

// interface ModuleCardProps {
//   module: Module;
//   menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
//   menuItems: any[];
//   chapterMenuItems: (ch: Chapter) => any[];
//   materialMenuItems: (m: StudyMaterial) => any[];
//   onViewMaterial: (m: StudyMaterial) => void;
// }

// const ModuleCard: React.FC<ModuleCardProps> = ({ module, menuRefs, menuItems, chapterMenuItems, materialMenuItems, onViewMaterial }) => (
//   <div style={{ marginBottom: '16px' }}>
//     <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', border: '2px solid #e0e0e0', borderRadius: '16px', padding: '20px', marginBottom: '12px' }}>
//       <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>

//         {/* Order badge */}
//         <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem', fontWeight: '700', color: '#d4f17e' }}>
//           {module.order}
//         </div>

//         <div style={{ flex: 1, minWidth: 0 }}>
//           <h3 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', fontWeight: '600', color: '#1a1a1a' }}>{module.title}</h3>
//           <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#666', lineHeight: '1.5' }}>{module.description}</p>
//           <div style={{ fontSize: '0.8rem', color: '#999' }}>
//             <i className="pi pi-book" style={{ marginRight: '4px' }}></i>
//             {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
//           </div>
//         </div>

//         <MenuButton menuKey={`module-${module.id}`} menuRefs={menuRefs} menuItems={menuItems} size="normal" />
//       </div>

//       {/* Chapters */}
//       {module.chapters.length > 0 && (
//         <div style={{ marginTop: '20px', paddingLeft: '66px' }}>
//           {module.chapters.map((chapter) => (
//             <ChapterCard
//               key={chapter.id}
//               chapter={chapter}
//               moduleOrder={module.order}
//               menuRefs={menuRefs}
//               menuItems={chapterMenuItems(chapter)}
//               materialMenuItems={materialMenuItems}
//               onViewMaterial={onViewMaterial}
//             />
//           ))}
//         </div>
//       )}
//     </div>
//   </div>
// );

// interface ChapterCardProps {
//   chapter: Chapter;
//   moduleOrder: number;
//   menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
//   menuItems: any[];
//   materialMenuItems: (m: StudyMaterial) => any[];
//   onViewMaterial: (m: StudyMaterial) => void;
// }

// const ChapterCard: React.FC<ChapterCardProps> = ({ chapter, moduleOrder, menuRefs, menuItems, materialMenuItems, onViewMaterial }) => (
//   <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
//     <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>

//       <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
//         {moduleOrder}.{chapter.order}
//       </div>

//       <div style={{ flex: 1, minWidth: 0 }}>
//         <h4 style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>{chapter.title}</h4>
//         <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#666' }}>{chapter.description}</p>
//         <div style={{ fontSize: '0.75rem', color: '#999' }}>
//           <i className="pi pi-file" style={{ marginRight: '4px' }}></i>
//           {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
//         </div>

//         {/* Study Materials */}
//         {chapter.study_materials && chapter.study_materials.length > 0 && (
//           <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
//             {chapter.study_materials.map((material) => (
//               <MaterialRow
//                 key={material.id}
//                 material={material}
//                 menuRefs={menuRefs}
//                 menuItems={materialMenuItems(material)}
//                 onView={() => onViewMaterial(material)}
//               />
//             ))}
//           </div>
//         )}
//       </div>

//       <MenuButton menuKey={`chapter-${chapter.id}`} menuRefs={menuRefs} menuItems={menuItems} size="small" />
//     </div>
//   </div>
// );

// interface MaterialRowProps {
//   material: StudyMaterial;
//   menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
//   menuItems: any[];
//   onView: () => void;
// }

// const MaterialRow: React.FC<MaterialRowProps> = ({ material, menuRefs, menuItems, onView }) => {
//   const readyVideos = material.videos?.filter(v => v.status === 'ready').length ?? 0;
//   const processingVideos = material.videos?.filter(v => v.status === 'processing').length ?? 0;

//   return (
//     <div
//       onClick={onView}
//       style={{ padding: '12px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease' }}
//       onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#0f3d3e'; }}
//       onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f9fa'; e.currentTarget.style.borderColor = '#e9ecef'; }}
//     >
//       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1a1a1a', marginBottom: '6px' }}>{material.title}</div>
//           <div style={{ fontSize: '0.75rem', color: '#999', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
//             {material.files_count > 0 && (
//               <span><i className="pi pi-paperclip" style={{ marginRight: '4px' }}></i>{material.files_count} file{material.files_count !== 1 ? 's' : ''}</span>
//             )}
//             {readyVideos > 0 && (
//               <span style={{ color: '#16a34a', fontWeight: '600' }}>
//                 <i className="pi pi-play-circle" style={{ marginRight: '4px' }}></i>{readyVideos} video{readyVideos !== 1 ? 's' : ''}
//               </span>
//             )}
//             {processingVideos > 0 && (
//               <span style={{ color: '#d97706' }}>
//                 <i className="pi pi-spin pi-spinner" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{processingVideos} processing
//               </span>
//             )}
//           </div>
//         </div>
//         <MenuButton menuKey={`material-${material.id}`} menuRefs={menuRefs} menuItems={menuItems} size="small" />
//       </div>
//     </div>
//   );
// };

// interface MenuButtonProps {
//   menuKey: string;
//   menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
//   menuItems: any[];
//   size: 'normal' | 'small';
// }

// const MenuButton: React.FC<MenuButtonProps> = ({ menuKey, menuRefs, menuItems, size }) => (
//   <>
//     <button
//       onClick={(e) => { e.stopPropagation(); menuRefs.current[menuKey]?.toggle(e); }}
//       style={{
//         padding: size === 'normal' ? '8px 12px' : '6px 10px',
//         background: '#f5f5f5', border: size === 'small' ? '1px solid #e0e0e0' : 'none',
//         borderRadius: size === 'normal' ? '8px' : '6px',
//         color: '#0f3d3e', cursor: 'pointer', flexShrink: 0
//       }}
//     >
//       <i className="pi pi-ellipsis-v" style={{ fontSize: size === 'normal' ? '0.85rem' : '0.75rem' }}></i>
//     </button>
//     <Menu model={menuItems} popup ref={(el) => { menuRefs.current[menuKey] = el; }} />
//   </>
// );

// export default StudyMaterials;